import { ApiResponse, IAreaDetailInput, IMapAutocompleteInput, IMapAutocompleteItemInput, IMapAutocompleteOutput, RequestMeta } from "@types";
import { commonApi } from "gate";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import EmptySuggestion from "./emptySuggestion";
import LoadingIcon from "@svg/loadingIcon";
import cn from "classnames"
import { useRouter } from "next/router";
import SearchIcon from "@svg/SearchIcon";
import axios from "axios";



interface Point {
  lat: number;
  lng: number;
}
interface MapAutocompleteProps {
  area: IAreaDetailInput;
  action: (v: { lat: number, lng: number }) => void
  boundaryPoints: Point[]
}
let req: RequestMeta<ApiResponse<IMapAutocompleteInput>, IMapAutocompleteOutput, never>
const MapAutocomplete: React.FC<MapAutocompleteProps> = ({ area, action, boundaryPoints }) => {
  const t = useTranslations()
  const { locale } = useRouter()
  const [searchValue, setSearchValue] = useState("")
  const [items, setItems] = useState<IMapAutocompleteItemInput[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showResult, setShowResult] = useState<boolean>(true)
  const [radius, setRadius] = useState<number>(100)

  useEffect(() => {
    if (area && boundaryPoints) {
      findFarthestPoint(area.center, boundaryPoints)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, boundaryPoints])

  useEffect(() => {
    setShowResult(true)
    if (searchValue.length > 2) {
      search()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])


  function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Radius of the Earth in meters
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in meters
    return distance;
  }

  function findFarthestPoint(currentPoint: Point, points: Point[]) {
    let maxDistance = -1;
    let farthestPoint: Point | null = null;

    for (let point of points) {
      const distance = calculateDistance(
        currentPoint.lat,
        currentPoint.lng,
        point.lat,
        point.lng
      );
      if (distance > maxDistance) {
        maxDistance = distance;
        farthestPoint = point;
      }
    }

    if (farthestPoint) {
      setRadius(maxDistance)
    } else {
      console.log("No points provided");
    }
  }
  const search = () => {
    setIsLoading(true);
    if (req?.cancelToken) {
      req.cancel();
    }
    req = commonApi.mapAutocomplete({
      "search": searchValue,
      "location": `${area.center.lat},${area.center.lng}`,
      "strictbounds": true,
      "radius": radius
    })

    req.fetch().then(res => {
      isInBoundary(res.data.items)
    }).catch(err => {
      console.log(err)
      setIsLoading(false)
    })
  }



  const isInBoundary = async (list: IMapAutocompleteItemInput[]) => {
    const items: IMapAutocompleteItemInput[] = [];
    for (const iterator of list) {
      try {
        const response = await axios.get(`/api/places/details?placeId=${iterator.place_id}`);
        const data = response.data;
        const { lat, lng } = data.result.geometry.location;
        if (isLocationWithinBoundary({ lat, lng })) {
          items.push(iterator);
        }
      } catch (error) {
        console.error(error);
      }
    }
    setItems(items);
    setIsLoading(false);
  };

  function isLocationWithinBoundary(location: Point) {
    let isInside = false;
    for (let i = 0, j = boundaryPoints.length - 1; i < boundaryPoints.length; j = i++) {
      const xi = boundaryPoints[i].lat;
      const yi = boundaryPoints[i].lng;
      const xj = boundaryPoints[j].lat;
      const yj = boundaryPoints[j].lng;
      const intersect = ((yi > location.lng) !== (yj > location.lng)) &&
        (location.lat < (xj - xi) * (location.lng - yi) / (yj - yi) + xi);
      if (intersect) {
        isInside = !isInside;
      }
    }
    return isInside;
  }

  const getDetail = (id: string) => {
    setIsLoading(true)
    commonApi.placeDetail({ place_id: id }).fetch().then(res => {
      action(res.data.location)
      setShowResult(false)
    }).catch(err => {
      console.log(err)
    }).finally(() => {
      setIsLoading(false)
    })

  }
  return (
    <div className="w-full relative">
      <div className="w-full relative">
        <div className={cn(" absolute h-14 top-0 flex items-center justify-center p-2 left-0")}>
          {isLoading ? <LoadingIcon /> : <SearchIcon />}
        </div>
        <input type="search" className="w-full h-14 rounded-2xl bg-[#F5F5F5] text-sm font-normal placeholder:text-[#aaa] placeholder:text-center outline-0 p-3 text-center" placeholder={t("client_myaddresses_searchaddress")} value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onClick={() => setShowResult(true)} />
      </div>
      {searchValue && showResult ? <div className=" w-full absolute top-16 left-0 bg-white rounded-2xl shadow-[0px_8px_24px_rgba(0,0,0,0.08)] h-72 scroll overflow-y-auto z-10 flex flex-col p-3">
        {items.length ?
          items.map((item) => (
            <div className={cn("w-full flex flex-col p-2 cursor-pointer hover:bg-[#F5F5F5] rounded-lg transition-all duration-300 ease-in-out", locale?.includes("ar-") ? "items-end" : "items-start")} key={item.place_id} onClick={() => getDetail(item.place_id)}>
              <p className=" text-textDark font-normal text-sm">{item.structured_formatting.main_text}</p>
              <p className=" text-textLight font-normal text-xs">{item.structured_formatting.secondary_text}</p>

            </div>
          )) : (
            <div className=" w-full h-full flex items-center justify-center">
              <EmptySuggestion />
            </div>
          )}
      </div> : null}
    </div>
  );
}

export default MapAutocomplete;